/**
 * @fileoverview Feature Flag Service for Progressive Feature Rollout
 * 
 * This module provides a feature flag system for controlling feature availability
 * across the application. Supports percentage-based rollouts, targeted users/orgs,
 * and client-side caching for performance.
 * 
 * @module lib/featureFlags
 * 
 * Key Features:
 * - Percentage-based gradual rollouts
 * - Targeted feature access by user or organization
 * - Client-side caching with TTL (5 minutes)
 * - Deterministic rollout based on user/org ID hashing
 * 
 * Common Use Cases:
 * 1. Beta feature testing with select users
 * 2. Gradual rollout of new features (10% → 50% → 100%)
 * 3. Organization-specific features
 * 4. Emergency killswitches for problematic features
 * 5. A/B testing different features
 * 
 * @example Check if feature enabled
 * ```typescript
 * const canUseNewEditor = await featureFlags.isEnabled(
 *   'new_editor',
 *   currentUserId,
 *   currentOrgId
 * );
 * 
 * if (canUseNewEditor) {
 *   return <NewEditor />;
 * }
 * ```
 * 
 * @example Gradual rollout
 * ```typescript
 * // In database: UPDATE feature_flags 
 * // SET rollout_percentage = 25 WHERE flag_name = 'ai_suggestions'
 * 
 * // 25% of users will see AI suggestions
 * const showAI = await featureFlags.isEnabled('ai_suggestions', userId);
 * ```
 * 
 * @example Targeted access
 * ```typescript
 * // In database: UPDATE feature_flags 
 * // SET target_users = '["user-123", "user-456"]' 
 * // WHERE flag_name = 'beta_dashboard'
 * 
 * // Only specified users get access regardless of rollout percentage
 * const canAccessBeta = await featureFlags.isEnabled('beta_dashboard', userId);
 * ```
 */

import { supabase } from '@/integrations/supabase/client'

/**
 * Feature flag configuration from database
 */
export interface FeatureFlag {
  id: string
  flag_name: string
  enabled: boolean
  rollout_percentage: number
  target_organizations: any[]
  target_users: any[]
  description?: string
  created_at: string
  updated_at: string
}

/**
 * Service for checking and managing feature flags
 * 
 * @remarks
 * - Caches flags client-side for 5 minutes to reduce database queries
 * - Uses deterministic hashing for consistent rollout percentages
 * - Call clearCache() after updating flags in admin panel
 */
class FeatureFlagService {
  private cache: Map<string, FeatureFlag> = new Map()
  private cacheExpiry: Map<string, number> = new Map()
  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  /**
   * Checks if a feature flag is enabled for a user/organization
   * 
   * @param flagName - Name of the feature flag to check
   * @param userId - Optional user ID for targeted access and rollout
   * @param organizationId - Optional organization ID for org-level access
   * @returns Promise<boolean> - true if feature is enabled
   * 
   * @example
   * ```typescript
   * // Check if user can access beta features
   * const betaAccess = await featureFlags.isEnabled(
   *   FEATURE_FLAGS.BETA_FEATURES,
   *   user.id,
   *   user.organizationId
   * );
   * ```
   * 
   * @remarks
   * Priority order:
   * 1. Flag must be enabled=true in database
   * 2. User ID in target_users list → always enabled
   * 3. Org ID in target_organizations list → always enabled
   * 4. rollout_percentage >= 100 → enabled for all
   * 5. rollout_percentage <= 0 → disabled for all
   * 6. Deterministic hash-based rollout using user/org ID
   */
  async isEnabled(flagName: string, userId?: string, organizationId?: string): Promise<boolean> {
    try {
      const flag = await this.getFlag(flagName)
      if (!flag || !flag.enabled) {
        return false
      }

      // Check if user/org is explicitly targeted
      if (userId && flag.target_users.includes(userId)) {
        return true
      }

      if (organizationId && flag.target_organizations.includes(organizationId)) {
        return true
      }

      // Check rollout percentage
      if (flag.rollout_percentage >= 100) {
        return true
      }

      if (flag.rollout_percentage <= 0) {
        return false
      }

      // Use deterministic rollout based on user/org ID
      const id = userId || organizationId || 'anonymous'
      const hash = this.simpleHash(id + flagName)
      const percentage = hash % 100
      
      return percentage < flag.rollout_percentage
    } catch (error) {
      console.error('Error checking feature flag:', error)
      return false
    }
  }

  /**
   * Retrieves a feature flag from database with caching
   * 
   * @param flagName - Name of feature flag
   * @returns FeatureFlag object or null if not found
   * 
   * @remarks
   * - Results are cached for 5 minutes
   * - Returns null if flag doesn't exist (treated as disabled)
   */
  async getFlag(flagName: string): Promise<FeatureFlag | null> {
    // Check cache first
    const cached = this.cache.get(flagName)
    const expiry = this.cacheExpiry.get(flagName)
    
    if (cached && expiry && Date.now() < expiry) {
      return cached
    }

    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('flag_name', flagName)
        .maybeSingle()

      if (error) {
        throw error
      }

      if (!data) {
        // Flag doesn't exist
        return null
      }

      // Cache the result
      const flag = data as FeatureFlag
      this.cache.set(flagName, flag)
      this.cacheExpiry.set(flagName, Date.now() + this.CACHE_TTL)

      return flag
    } catch (error) {
      console.error('Error fetching feature flag:', error)
      return null
    }
  }

  /**
   * Retrieves all feature flags from database
   * 
   * @returns Array of all feature flags
   * 
   * @remarks
   * Used primarily in admin panels for flag management
   */
  async getAllFlags(): Promise<FeatureFlag[]> {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('flag_name')

      if (error) throw error
      return (data as FeatureFlag[]) || []
    } catch (error) {
      console.error('Error fetching all feature flags:', error)
      return []
    }
  }

  /**
   * Clears the feature flag cache
   * 
   * @param flagName - Optional specific flag to clear, or all if omitted
   * 
   * @remarks
   * Call this after updating flags in admin panel to see changes immediately
   */
  clearCache(flagName?: string): void {
    if (flagName) {
      this.cache.delete(flagName)
      this.cacheExpiry.delete(flagName)
    } else {
      this.cache.clear()
      this.cacheExpiry.clear()
    }
  }

  /**
   * Simple hash function for deterministic rollout
   * 
   * @internal
   * @param str - String to hash (typically userId + flagName)
   * @returns Positive integer hash value
   * 
   * @remarks
   * Ensures same user gets same result for a flag (consistent experience)
   */
  private simpleHash(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }
}

/**
 * Singleton instance of FeatureFlagService
 * 
 * @example
 * ```typescript
 * import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';
 * 
 * const isReadOnly = await featureFlags.isEnabled(
 *   FEATURE_FLAGS.READ_ONLY_MODE
 * );
 * ```
 */
export const featureFlags = new FeatureFlagService()

/**
 * Common feature flag names used across the application
 * 
 * @remarks
 * Add new flags here for type safety and discoverability
 */
export const FEATURE_FLAGS = {
  // System flags
  READ_ONLY_MODE: 'read_only_mode',
  MAINTENANCE_MODE: 'maintenance_mode',
  WAITLIST_MODE: 'waitlist_mode',
  
  // UI Visibility flags
  /** Controls visibility of the calendar booking/demo scheduling section on Contact page */
  SHOW_CALENDAR_BOOKING: 'show_calendar_booking',
  /** Controls visibility of the live chat section on Contact page */
  SHOW_LIVE_CHAT: 'show_live_chat',
  /** Controls visibility of the global search bar in the navigation header */
  GLOBAL_SEARCH: 'global_search',
  /** Controls visibility of the tax exemption section on the client profile page */
  TAX_EXEMPTION_MANAGEMENT: 'tax_exemption_management',
  
  // Access control flags
  /** Controls whether mobile devices are blocked from accessing the platform */
  MOBILE_DEVICE_RESTRICTION: 'mobile_device_restriction',
  
  // Verification flags
  ENHANCED_VERIFICATION: 'enhanced_verification',
  PREMIUM_VERIFICATION: 'premium_verification',
  
  // Document requirement flags
  /** Controls whether Business Registration document is required for vendor verification */
  REQUIRE_BUSINESS_REGISTRATION: 'require_business_registration',
  /** Controls whether W-9 document is required for vendor verification */
  REQUIRE_W9: 'require_w9',

  // Payment flags
  /** Controls whether payment processing (Stripe, ACH, payouts, refunds) is enabled */
  PAYMENT_PROCESSING_ENABLED: 'payment_processing_enabled',
} as const